import React, { useRef, forwardRef, useImperativeHandle } from 'react';
import MultipleZoneSelect from '../inputs/MultipleZoneSelect';
import {
    normalizeParts,
    trimTrailingSeparators,
    ENTITY_PART_TYPE_SEPARATOR,
} from '../../../models/common/EntityPart';
import {
    createSeparatorZonePart,
    ZONE_SEPARATOR_CHARACTERS,
} from '../../../models/ZonePart';

const MultiZoneEditor = forwardRef((props, ref) => {
    const selectRef = useRef(null);
    const defaultSep =
        props.defaultSeperatorCharacter || ZONE_SEPARATOR_CHARACTERS[0];

    const handleOnTab = () => props.api.tabToNextCell();
    const handleOnTabBack = () => props.api.tabToPreviousCell();
    const handleOnEnter = () => props.stopEditing();

    useImperativeHandle(ref, () => ({
        getValue() {
            const rawParts = selectRef.current?.formattedValue ?? [];
            if (rawParts.length === 0) {
                // If the editor was seeded with a pre-existing value and the user
                // cleared it, return [] to signal an intentional clear.
                // The zone compare strategy treats null vs [] as different so the
                // save fires. If the editor started empty, return null (no change).
                return effectiveValue?.length > 0 ? [] : null;
            }
            const normalized = normalizeParts(
                rawParts,
                createSeparatorZonePart,
                defaultSep
            );
            const trimmed = trimTrailingSeparators(normalized);
            const zones = trimmed
                .filter((p) => p.partType !== ENTITY_PART_TYPE_SEPARATOR)
                .map((p) => p.value);
            return zones.length > 0
                ? zones
                : effectiveValue?.length > 0
                ? []
                : null;
        },
        isPopup() {
            return true;
        },
        afterGuiAttached() {
            selectRef.current?.focus();
        },
    }));

    // props.value is the array field (e.g. loadZones). For unmigrated rows it may be
    // null — fall back to the singular field (e.g. loadZone) via row data.
    // Do NOT fall back when props.value is [] (explicitly cleared).
    const field = props.column.colDef.field;
    const singularField = field?.replace(/Zones$/, 'Zone');
    const singularValue =
        props.value == null && singularField && props.data
            ? props.data[singularField]
            : null;
    const effectiveValue =
        props.value?.length > 0
            ? props.value
            : props.value == null && singularValue
            ? [singularValue]
            : props.value;

    return (
        <MultipleZoneSelect
            className="location-editor"
            ref={selectRef}
            context={props.context}
            field={field}
            headerName={props.column.colDef.headerName}
            initialChar={props.charPress}
            value={effectiveValue}
            onTab={handleOnTab}
            onTabBack={handleOnTabBack}
            onEnter={handleOnEnter}
            defaultSeperatorCharacter={defaultSep}
            blockedCharacters={props.blockedCharacters}
        />
    );
});

export default MultiZoneEditor;
